import React, { createContext, useEffect, useState } from "react"
// import { CognitoUser } from "@aws-amplify/auth"
import { Amplify, Auth, API } from "aws-amplify"
import awsconfig from "../aws-exports"
import { updateTeammate } from "../graphql/mutations"
import { CognitoUserInterface } from "@aws-amplify/ui-components"

Amplify.configure(awsconfig)
interface UserAddress {
  formatted?: string
  street_address?: string
  locality?: string
  region?: string
  postal_code?: string
  country?: string
  place_id?: string
}

export interface UserAttributes {
  address?: UserAddress
  email: string
  phone_number?: string
  locale?: string
  name?: string
  given_name?: string
  family_name?: string
  picture?: string
  profile?: string
  sub?: string
  website?: string
  reCapthcaToken?: string
  "custom:storage"?: string
  "custom:creation_role"?: string
  "custom:organization"?: string
  "custom:organizationID"?: string
  "custom:creation_industry"?: string
  "custom:profile_progress"?: number
  "custom:payment_progress"?: number
  "custom:plan_progress"?: number
  "custom:configure_progress"?: number
}

interface CognitoUserExt extends CognitoUserInterface {
  attributes: UserAttributes
}

interface AuthState {
  cognitoUser: CognitoUserExt | undefined
  OTPChallenge: boolean
  OTPExpired: boolean
  sendOTP: (username: string) => Promise<void>
  confirmOTP: (otp: string) => Promise<void>
  signUp: (newUser: UserAttributes) => Promise<void>
  signOut: () => Promise<void>
  updateUserAttributes: (attributes: any) => Promise<void>
  isUserSignedIn: () => Promise<boolean>
  getCurrentUserProfileValue: () => string
  updateTeammateAttributes: (teammateAttributes: any) => Promise<void>
}

export const AuthContext = createContext<AuthState>({
  cognitoUser: {
    attributes: { email: "" },
    challengeName: "",
    challengeParam: {},
  },
  OTPChallenge: false,
  OTPExpired: false,
  confirmOTP: async () => {},
  sendOTP: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  updateUserAttributes: async () => {},
  isUserSignedIn: async () => false,
  getCurrentUserProfileValue: () => "",
  updateTeammateAttributes: async () => {},
})

interface AuthContextProps {
  children: React.ReactNode
}

function getRandomString(bytes: number) {
  const randomValues = new Uint8Array(bytes)
  window.crypto.getRandomValues(randomValues)
  return Array.from(randomValues).map(intToHex).join("")
}

function intToHex(nr: number) {
  return nr.toString(16).padStart(2, "0")
}

const AuthContextProvider = ({ children }: AuthContextProps) => {
  // const { setCheckUserSignIn } = useContext(AppContext)
  const [cognitoUser, setCognitoUser] = useState<CognitoUserExt>()
  const [OTPChallenge, setOTPChallenge] = useState<boolean>(false)
  const [OTPExpired, setOTPExpired] = useState<boolean>(false)

  useEffect(() => {
    const getUser = async () => {
      await checkSignedIn()
    }
    if (!cognitoUser) {
      try {
        getUser()
      } catch (err) {
        // console.log("USERCONTEXT:: User not found")
      }
    }
  }, [cognitoUser])

  useEffect(() => {
    const getUser = async () => {
      try {
        const authData: CognitoUserExt = await Auth.currentAuthenticatedUser()
        // console.log("authenticated user", authData)
        setCognitoUser(authData)
      } catch {
        // console.log("anonymous user")
      }
    }
    try {
      getUser()
    } catch (err) {
      // console.log("USERCONTEXT:: Initial user anonymous")
    }
  }, [])

  const checkSignedIn = async () => {
    try {
      const authData: CognitoUserExt = await Auth.currentAuthenticatedUser()
      // console.log("authenticated user", authData)
      setCognitoUser(authData)
    } catch {
      // console.log("anonymous user")
    }
  }

  const isUserSignedIn = async () => {
    try {
      await Auth.currentAuthenticatedUser()
      return true
    } catch {
      return false
    }
  }

  const signUp = async (newUser: UserAttributes) => {
    await Auth.signUp({
      username: newUser.email.toLowerCase(),
      password: getRandomString(30),
      attributes: {
        email: newUser.email.toLowerCase(),
        name: newUser.name,
        given_name: newUser.given_name,
        family_name: newUser.family_name,
        "custom:organization": newUser["custom:organization"],
        "custom:creation_role": "admin",
      },
      validationData: {
        v2InvisibleCaptcha: newUser.reCapthcaToken,
      },
    })
  }

  const sendOTP = async (username: string) => {
    // console.log("sendOTP username", username)
    try {
      await signOut()
      const cognitoUserResponse = await Auth.signIn(username)
      setOTPChallenge(true)
      setOTPExpired(false)
      setCognitoUser(cognitoUserResponse)
      // console.log('cognitoUserResponse', cognitoUserResponse)
      // console.log("✅ OTP sent successfully!")
    } catch (error) {
      // console.log("AUTHCONTEXT: ERROR SIGNIN", JSON.stringify(error, null, 2))
      throw error
    }
  }

  const confirmOTP = async (otp: string) => {
    try {
      // Send the answer to the User Pool
      // This will throw an error if it’s the 3rd wrong answer
      await Auth.sendCustomChallengeAnswer(cognitoUser, otp)
      // console.log('cognitoUserResponse',cognitoUserResponse)
    } catch (error) {
      // console.log(
      //   "AUTHCONTEXT: sendCustomChallengeAnswer ERROR",
      //   JSON.stringify(error, null, 2)
      // )
      // console.log(error)
      setOTPExpired(true)
      setOTPChallenge(false)
      throw {
        code: "NotAuthorizedException",
        message: "Your verification code has expired.",
        name: "NotAuthorizedException",
      }
    }

    // It we get here, the answer was sent successfully,
    // but it might have been wrong (1st or 2nd time)
    // So we should test if the user is authenticated now
    try {
      // This will throw an error if the user is not yet authenticated:
      // const possibleSession = await Auth.currentSession()
      // console.log("possibleSession", possibleSession)
      await checkSignedIn()
      // setOTPChallenge(false)
      setOTPExpired(false)
    } catch (error) {
      // console.log(
      //   "AUTHCONTEXT: currentSession ERROR",
      //   JSON.stringify(error, null, 2)
      // )
      throw {
        code: "NotAuthorizedException",
        message: "Invalid Access Code.",
        name: "NotAuthorizedException",
      }
    }
  }

  const signOut = async () => {
    try {
      setOTPChallenge(false)
      setOTPExpired(false)
      await Auth.signOut()
      setCognitoUser(undefined)
      // console.log("✅ Cognito Sign-out successfully!")
    } catch (error) {
      // console.log(
      //   "AUTHCONTEXT: ERROR SIGNING OUT",
      //   JSON.stringify(error, null, 2)
      // )
    }
  }

  const updateUserAttributes = async (attributes: any) => {
    // console.log("updating attributes:", attributes)
    try {
      const currentUser = await Auth.currentAuthenticatedUser()
      await Auth.updateUserAttributes(currentUser, attributes)
      setCognitoUser(user => {
        if (user) {
          user.attributes = { ...user?.attributes, ...attributes }
          return user
        }
      })
      // console.log("✅ User attributes updated successfully!")
    } catch (error) {
      // console.log(
      //   "AUTHCONTEXT: ERROR UPDATING USER ATTRIBUTES",
      //   JSON.stringify(error, null, 2)
      // )
    }
  }

  const updateTeammateAttributes = async (teamAttributes: any) => {
    let userID
    try {
      const cognitoUserResponse = await Auth.currentAuthenticatedUser()
      if (cognitoUserResponse) {
        userID = cognitoUserResponse.attributes.sub
      }
    } catch (error) {
      console.log(
        "Unable to authenticate Teammate attributes for logged out user"
      )
    }
    let updateInput: any = { id: userID, name: teamAttributes.name }
    // console.log("updateAttributes:", updateInput)
    const updateQuery = {
      query: updateTeammate,
      variables: {
        input: updateInput,
      },
    }

    try {
      const {
        data: { updateTeammate: response },
      } = await (API.graphql(updateQuery) as Promise<any>)
      console.log("Update Teammate response", response)
    } catch (error) {
      console.log("Unable to update Teammate", error)
    }
  }

  const getCurrentUserProfileValue = () => {
    const profile = cognitoUser?.attributes?.profile
    if (profile) {
      return profile
    } else {
      // console.log("no profile value found: ", cognitoUser)
      return ""
    }
  }

  return (
    <AuthContext.Provider
      value={{
        cognitoUser,
        OTPChallenge,
        OTPExpired,
        sendOTP,
        confirmOTP,
        signUp,
        signOut,
        updateUserAttributes,
        isUserSignedIn,
        getCurrentUserProfileValue,
        updateTeammateAttributes,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContextProvider
